// MainPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Pickaxe, Twitter, AlertTriangle, Brain, ChevronRight, Search, Star, TrendingUp, Calendar, Activity, ScanSearch, Newspaper, DollarSign, Zap, Info, MessageSquare, Smartphone, X, Download, Mail, Globe, User, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import SearchModal from './SearchModal/SearchModal'; // 새로운 SearchModal 컴포넌트 import
import { AnimatedCircularProgress } from './Progress/AnimatedCircularProgress';
import FullAnalysisModal from './FullAnalysisModal/FullAnalysisModal';
import './StockMainPage.css';
import NewsSection from './News/NewsSection';
import MiniTradingView from './TradingView/MiniTradingView';
// import HeatMapTradingView from './TradingView/HeatMapTradingView';
import InvestmentDisclaimer from './Common/InvestmentDisclaimer';
import ContactFooter from './Common/ContactFooter';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import RankingSection from './Ranking/RankingSection';
import RankingModal from './Ranking/RankingModal';
import OnboardingModal from './Onboarding/OnboardingModal';
import RedditBuzzBubble from './RedditBuzz/RedditBuzzBubble';
import PolymarketBuzz from './PolymarketBuzz/PolymarketBuzz';
import FearGreedHistoryChart from './FearGreedHistory/FearGreedHistoryChart';
import RecessionAlert from './RecessionAlert/RecessionAlert';
import { getDashboardMainContent, getDashboardMarketData, getFavoriteList, getXTweetData, getFearGreedIndex, getStockRanking, getRefreshToken, isTokenExpired, isOAuthUser, getPlatform, initNativeToken, updateNativeRefreshToken, getAppVersion, isVersionBelow } from '../services/api';
import AlertModal, { useAlert } from './Common/Alert';

const StockMainPage = () => {
  const { theme, toggleTheme } = useTheme();
  const { alert, showAlert, closeAlert } = useAlert();

  const [hasFavorites, setHasFavorites] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false); // 검색 모달 상태 추가
  const [isFullAnalysisModalOpen, setIsFullAnalysisModalOpen] = useState(false);
  const [fearGreedData, setFearGreedData] = useState(null);
  const [mainContentData, setMainContentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainContentLoading, setMainContentLoading] = useState(true);
  const [marketData, setMarketData] = useState(null);
  const [marketDataLoading, setMarketDataLoading] = useState(true);

  const [favoriteList, setFavoriteList] = useState([]);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [showAppDownloadSheet, setShowAppDownloadSheet] = useState(false);
  const [xTweetData, setXTweetData] = useState([]);
  const [xTweetLoading, setXTweetLoading] = useState(true);
  const [rankingData, setRankingData] = useState(null);
  const [rankingLoading, setRankingLoading] = useState(true);
  const [showFullRanking, setShowFullRanking] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const navigate = useNavigate();
  
  // 주식 로고 이미지 경로 생성
  const getStockLogoPath = (ticker) => {
    return `/CompanyLogo/${ticker}.svg`;
  };

  const handleClick = (stockName) => {
    navigate(`/stockDetail/${stockName}/`);
  };
  // 검색 모달 열기
  const openSearchModal = (e) => {
    // 이벤트 객체가 있는 경우 즉시 기본 동작 방지
    console.log(e, 'Search modal open triggered');
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.preventDefault();
      e.nativeEvent?.stopImmediatePropagation();
    }
    
    // 즉시 상태 업데이트 (setTimeout 제거)
    setIsSearchModalOpen(true);
    
    return false; // 추가적인 기본 동작 방지
  };

  // 검색 모달 닫기
  const closeSearchModal = (e) => {
    console.log(e, 'Search modal open triggered');
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.preventDefault();
      e.nativeEvent?.stopImmediatePropagation();
    }

    setIsSearchModalOpen(false);
    return false; // 추가적인 기본 동작 방지
  };

  // 프로필 페이지로 이동
  const goToProfile = (e) => {
    e.preventDefault();
    navigate('/profile');
  };

  // 앱 다운로드 bottom sheet 닫기
  const closeAppDownloadSheet = () => {
    setShowAppDownloadSheet(false);
    localStorage.setItem('hasSeenAppPromo', 'true');
  };

  // 앱 스토어로 이동
  const goToAppStore = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/android/i.test(userAgent)) {
      // Android - Google Play Store
      window.open('https://play.google.com/store/apps/details?id=com.makemoneystudio.isf', '_self');
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      // iOS - App Store
      window.open('https://apps.apple.com/app/6748879356', '_self');
    } else {
      // 기본적으로 Google Play Store로 이동
      window.open('https://play.google.com/store/apps/details?id=com.makemoneystudio.isf', '_self');
    }
    
    closeAppDownloadSheet();
  };


  // refreshToken 기준 인증 초기화
  const initAuth = async (isOAuthSuccess = false) => {
    console.log('[initAuth] start, isOAuthSuccess:', isOAuthSuccess);

    if (!isOAuthSuccess) {
      // 일반 진입: 네이티브에서 refreshToken 가져와 저장
      console.log('[initAuth] calling initNativeToken...');
      await initNativeToken();
      console.log('[initAuth] initNativeToken done');
    }

    const refreshToken = getRefreshToken();
    console.log('[initAuth] refreshToken:', refreshToken ? refreshToken.substring(0, 20) + '...' : null);
    console.log('[initAuth] isOAuthUser:', isOAuthUser());

    if (!isOAuthUser()) {
      console.log('[initAuth] not OAuth user → exit');
      return;
    }

    // OAuth 성공 직후: 서버 쿠키의 refreshToken을 앱에 저장
    if (isOAuthSuccess) {
      if (refreshToken) {
        console.log('[initAuth] calling updateNativeRefreshToken');
        updateNativeRefreshToken(refreshToken);
      } else {
        console.log('[initAuth] OAuth success but no refreshToken found!');
      }
    }

    console.log('[initAuth] calling fetchFavoriteList');
    await fetchFavoriteList();
    console.log('[initAuth] done');
  };

  // 즐겨찾기 목록 가져오기 함수
  const fetchFavoriteList = async () => {
    try {
      setFavoriteLoading(true);
      const data = await getFavoriteList();
      setFavoriteList(data.list || []);
      setHasFavorites(data.list && data.list.length > 0);
      console.log('Favorite list received:', data.list);
    } catch (error) {
      console.error('Error fetching favorite list:', error);
      setFavoriteList([]);
      setHasFavorites(false);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // 공포 탐욕 지수 API 호출
  const fetchFearGreedIndexData = async () => {
    try {
      setLoading(true);
      const data = await getFearGreedIndex();
      setFearGreedData(data.fearAndGreed);
    } catch (error) {
      console.error('Error fetching fear and greed index:', error);
    } finally {
      setLoading(false);
    }
  };

  // 대시보드 메인 컨텐츠 API 호출
  const fetchMainContent = async () => {
    try {
      setMainContentLoading(true);
      const data = await getDashboardMainContent();
      setMainContentData(data);
    } catch (error) {
      console.error('Error fetching main content:', error);
    } finally {
      setMainContentLoading(false);
    }
  };

  // 시장 데이터 API 호출
  const fetchMarketData = async () => {
    try {
      setMarketDataLoading(true);
      const data = await getDashboardMarketData();
      setMarketData(data);
    } catch (error) {
      console.error('Error fetching market data:', error);
    } finally {
      setMarketDataLoading(false);
    }
  };

  // X 트윗 데이터 API 호출
  const fetchXTweetData = async () => {
    try {
      setXTweetLoading(true);
      const data = await getXTweetData();
      setXTweetData(data.list || []);
    } catch (error) {
      console.error('Error fetching X tweet data:', error);
    } finally {
      setXTweetLoading(false);
    }
  };

  // 주식 랭킹 데이터 API 호출
  const fetchRankingData = async () => {
    try {
      setRankingLoading(true);
      const data = await getStockRanking();
      setRankingData(data);
    } catch (error) {
      console.error('Error fetching stock ranking:', error);
    } finally {
      setRankingLoading(false);
    }
  };

  // 신규 유저 온보딩 체크 (비활성화)
  const checkFirstVisitOnboarding = () => {
    // setShowOnboarding(true);
  };

  const handleOnboardingClose = () => {
    setShowOnboarding(false);
    localStorage.setItem('hasSeenOnboarding', 'true');
  };

  const handleKakaoShare = () => {
    const kakao = window.Kakao;
    if (!kakao) return;

    if (!kakao.isInitialized()) {
      kakao.init('3a612caf3635160cef3ec4542c0b8344');
    }

    const score = mainContentData?.score ?? '-';
    const summary = mainContentData?.aiSummary
      ? mainContentData.aiSummary.replace(/[#*_~`>]/g, '').trim().slice(0, 100) + '...'
      : '오늘의 AI 시장 분석을 확인하세요.';

    kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: `투자특공대 AI 시장 분석 · 오늘의 점수: ${score}점`,
        description: summary,
        imageUrl: `${window.location.origin}/social-share.png`,
        link: {
          webUrl: 'https://mmstudio.co.kr',
          mobileWebUrl: 'https://mmstudio.co.kr/appStore',
        },
      },
      buttons: [
        {
          title: '앱에서 보기',
          link: {
            webUrl: 'https://mmstudio.co.kr',
            mobileWebUrl: 'https://mmstudio.co.kr/appStore',
            // androidExecutionParams: `ticker=main`,
            // iosExecutionParams: `ticker=main`,
          },
        },
      ],
    });
  };

  // 웹 사용자 첫 방문 체크 및 앱 다운로드 유도
  const checkWebFirstVisit = () => {
    const platform = getPlatform();
    if (platform === 'ios' || platform === 'android') return;

    setTimeout(() => {
      const hasSeenAppPromo = localStorage.getItem('hasSeenAppPromo');
      if (!hasSeenAppPromo) {
        setShowAppDownloadSheet(true);
      }
    }, 4000);
  };

  // 각 섹션의 렌더링 함수들
  // const renderHeatmapSection = () => (
  //   <section className="section">
  //     <div className="favorites-grid">
  //       <div className="relative">
  //         <HeatMapTradingView></HeatMapTradingView>

  //         {/* 데이터 기준 안내 툴팁 */}
  //         <div className="absolute top-4 left-0 z-9999">
  //           <button
  //             className="relative"
  //             onMouseEnter={() => setShowTooltip('heatmap')}
  //             onMouseLeave={() => setShowTooltip(null)}
  //             onClick={() => setShowTooltip(showTooltip === 'heatmap' ? null : 'heatmap')}
  //           >
  //             <div className="w-5 h-5 bg-gray-600 bg-opacity-70 hover:bg-opacity-90 rounded-full flex items-center justify-center transition-all duration-200">
  //               <Info size={12} className="text-white" />
  //             </div>
  //           </button>
  //         </div>
  //       </div>
  //     </div>
  //   </section>
  // );

  const renderAiAnalysisSection = () => (
    <div className="feature-section">
      <div className="flex items-center justify-between gap-1 mb-1">
        {/* 좌측: 아이콘 + 제목 + 설명글 */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="feature-icon">
              <Brain size={20} />
            </div>
            <h2 className="text-xm font-bold text-black">시장 분석</h2>
          </div>
          <p className="text-xs text-gray-600 pl-1">
            국제 정세와 시장 심리를 한눈에 파악하세요
          </p>
        </div>

        {/* 우측: 스코어 */}
        <AnimatedCircularProgress
            key={`main-score-${mainContentData?.score || 0}`}
            score={mainContentData?.score || 0}
            size={76}
            delay={300}
          />
      </div>

      {mainContentLoading ? (
        <div className="ai-summary-card loading animate-pulse">
          <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
          <div className="h-3 bg-gray-100 rounded mb-2"></div>
          <div className="h-3 bg-gray-100 rounded mb-2"></div>
          <div className="h-3 bg-gray-100 rounded w-2/3"></div>
        </div>
      ) : (
        <div className="ai-summary-card">
          <div className="ai-summary-header">
            <div className="flex items-center gap-1.5">
              <span className="ai-badge"><Zap size={9} />AI</span>
              <span className="ai-summary-title">핵심 요약</span>
            </div>
            <button
              className="kakao-share-btn"
              onClick={handleKakaoShare}
              disabled={!mainContentData?.score}
            >
              <img
                src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png"
                alt="카카오톡 공유"
                style={{ width: 14, height: 14 }}
              />
              공유
            </button>
          </div>
          <p className="ai-summary-text">
            {mainContentData?.aiSummary || '시장 분석 데이터를 불러오는 중입니다...'}
          </p>
        </div>
      )}

      {marketDataLoading ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded mb-3 w-32"></div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      ) : marketData ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Activity size={16} className="text-indigo-600" />
            국제 정세 스냅샷
          </h3>

      {/* 주요 지표 요약 */}
      {marketDataLoading ? (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[...Array(10)].map((_, index) => (
            <div key={index} className="metric-indicator-card animate-pulse">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-3 h-3 bg-gray-200 rounded"></div>
                <div className="h-2.5 bg-gray-200 rounded w-14"></div>
              </div>
              <div className="flex items-baseline justify-between">
                <div className="h-4 bg-gray-200 rounded w-14"></div>
                <div className="h-3 bg-gray-200 rounded w-10"></div>
              </div>
            </div>
          ))}
        </div>
      ) : marketData ? (
        <div className="grid grid-cols-2 gap-2 mb-4">

          {/* VIX 공포지수 */}
          <div className="metric-indicator-card" style={{borderLeftColor: marketData.vix > 20 ? 'rgba(239,68,68,0.45)' : marketData.vix > 15 ? 'rgba(249,115,22,0.45)' : 'rgba(22,163,74,0.45)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <AlertTriangle size={13} className={marketData.vix > 20 ? 'text-red-500' : marketData.vix > 15 ? 'text-orange-500' : 'text-green-500'} />
                <span className="metric-label">VIX 지수</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('vix')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'vix' && (
                  <div className="metric-tooltip" style={{right: 'auto', left: 0}}>
                    <div className="metric-tooltip-title">😨 VIX 공포지수</div>
                    <div className="metric-tooltip-desc">투자자들의 공포 정도를 측정하는 지표로, 시장 변동성과 불안감을 나타냅니다.</div>
                    <div className="metric-tooltip-item">• 10-20: 안정적</div>
                    <div className="metric-tooltip-item">• 20-30: 불안함</div>
                    <div className="metric-tooltip-item">• 30이상: 극도 공포</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className={`metric-value ${marketData.vix > 20 ? 'text-red-600' : marketData.vix > 15 ? 'text-orange-600' : 'text-green-600'}`}>
                {marketData.vix?.toFixed(1)}
              </span>
              <span className={marketData.changes?.vix > 0 ? 'metric-change-down' : 'metric-change-up'}>
                {marketData.changes?.vix > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.vix || 0).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* S&P 500 */}
          <div className="metric-indicator-card" style={{borderLeftColor: 'rgba(59,130,246,0.45)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <TrendingUp size={13} className="text-blue-500" />
                <span className="metric-label">S&P 500</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('spx')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'spx' && (
                  <div className="metric-tooltip">
                    <div className="metric-tooltip-title">📈 S&P 500 지수</div>
                    <div className="metric-tooltip-desc">미국 대형주 500개 기업의 주가를 종합한 지수로, 미국 전체 주식시장의 80%를 대표합니다.</div>
                    <div className="metric-tooltip-item">• 상승: 미국 경제 호조, 글로벌 위험자산 선호</div>
                    <div className="metric-tooltip-item">• 하락: 미국 경제 우려, 글로벌 주식 부진</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="metric-value text-blue-600">
                {marketData.spx?.toLocaleString('ko-KR', {maximumFractionDigits: 0})}
              </span>
              <span className={marketData.changes?.spx > 0 ? 'metric-change-up' : 'metric-change-down'}>
                {marketData.changes?.spx > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.spx || 0).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* 러셀 2000 */}
          <div className="metric-indicator-card" style={{borderLeftColor: 'rgba(184,167,232,0.55)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <TrendingUp size={13} className="text-purple-400" />
                <span className="metric-label">러셀 2000</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('russell')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'russell' && (
                  <div className="metric-tooltip" style={{right: 'auto', left: 0}}>
                    <div className="metric-tooltip-title">🏢 러셀 2000 지수</div>
                    <div className="metric-tooltip-desc">미국 소형주 2000개 기업의 주가 지수로, '동네 상권' 같은 서민경제를 대표합니다.</div>
                    <div className="metric-tooltip-item">• 대형주 대비 상승: 경기 회복 기대감</div>
                    <div className="metric-tooltip-item">• 대형주 대비 하락: 경기 우려, 안전 선호</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="metric-value text-purple-600">
                {marketData.russell2k?.toLocaleString('ko-KR', {maximumFractionDigits: 0})}
              </span>
              <span className={marketData.changes?.russell2k > 0 ? 'metric-change-up' : 'metric-change-down'}>
                {marketData.changes?.russell2k > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.russell2k || 0).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* 달러 지수 */}
          <div className="metric-indicator-card" style={{borderLeftColor: 'rgba(22,163,74,0.45)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <DollarSign size={13} className="text-green-500" />
                <span className="metric-label">달러 지수</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('dxy')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'dxy' && (
                  <div className="metric-tooltip">
                    <div className="metric-tooltip-title">💵 달러 지수 (DXY)</div>
                    <div className="metric-tooltip-desc">달러의 강세를 측정하는 지표로, 주요 6개 통화 대비 달러 가치를 나타냅니다.</div>
                    <div className="metric-tooltip-item">• 상승: 달러 강세, 금 약세, 신흥국 부진</div>
                    <div className="metric-tooltip-item">• 하락: 달러 약세, 금 강세, 신흥국 호조</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="metric-value text-green-600">
                {marketData.dxy?.toFixed(1)}
              </span>
              <span className={marketData.changes?.dxy > 0 ? 'metric-change-up' : 'metric-change-down'}>
                {marketData.changes?.dxy > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.dxy || 0).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* 금 */}
          <div className="metric-indicator-card" style={{borderLeftColor: 'rgba(217,119,6,0.45)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Pickaxe size={13} className="text-yellow-600" />
                <span className="metric-label">금</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('gold')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'gold' && (
                  <div className="metric-tooltip" style={{right: 'auto', left: 0}}>
                    <div className="metric-tooltip-title">🥇 금 가격 (Gold)</div>
                    <div className="metric-tooltip-desc">대표적인 안전자산으로, 인플레이션과 경제 불안정 시기에 선호됩니다.</div>
                    <div className="metric-tooltip-item">• 상승: 인플레이션 우려, 달러 약세</div>
                    <div className="metric-tooltip-item">• 하락: 경제 안정, 달러 강세</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="metric-value text-yellow-700">
                ${marketData.gold_price?.toLocaleString('ko-KR', {maximumFractionDigits: 0})}
              </span>
              <span className={marketData.changes?.gold > 0 ? 'metric-change-up' : 'metric-change-down'}>
                {marketData.changes?.gold > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.gold || 0).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* WTI 원유 */}
          <div className="metric-indicator-card" style={{borderLeftColor: 'rgba(55,65,81,0.35)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-gray-700 flex-shrink-0"></div>
                <span className="metric-label">WTI 원유</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('wti')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'wti' && (
                  <div className="metric-tooltip">
                    <div className="metric-tooltip-title">🛢️ WTI 원유</div>
                    <div className="metric-tooltip-desc">인플레이션의 '대장' 역할을 하는 핵심 에너지 원자재로, 전 세계 물가에 직접 영향을 미칩니다.</div>
                    <div className="metric-tooltip-item">• 상승: 물가 전반 상승 압력</div>
                    <div className="metric-tooltip-item">• 하락: 인플레이션 압력 감소</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="metric-value text-gray-800">
                ${marketData.wti_price?.toFixed(1)}
              </span>
              <span className={marketData.changes?.wti_oil > 0 ? 'metric-change-up' : 'metric-change-down'}>
                {marketData.changes?.wti_oil > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.wti_oil || 0).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* 구리 */}
          <div className="metric-indicator-card" style={{borderLeftColor: 'rgba(234,88,12,0.45)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Pickaxe size={13} className="text-orange-500" />
                <span className="metric-label">구리</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('copper')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'copper' && (
                  <div className="metric-tooltip" style={{right: 'auto', left: 0}}>
                    <div className="metric-tooltip-title">⚡ 구리 (Doctor Copper)</div>
                    <div className="metric-tooltip-desc">경제학 박사 닉네임을 가진 원자재로, 건설·전자·자동차 등 모든 산업에 필수입니다.</div>
                    <div className="metric-tooltip-item">• 상승: 경기 회복, 산업 호황</div>
                    <div className="metric-tooltip-item">• 하락: 경기 둔화, 수요 감소</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="metric-value text-orange-700">
                ${marketData.copper_price?.toFixed(3)}
              </span>
              <span className={marketData.changes?.copper > 0 ? 'metric-change-up' : 'metric-change-down'}>
                {marketData.changes?.copper > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.copper || 0).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* USD/JPY */}
          <div className="metric-indicator-card" style={{borderLeftColor: 'rgba(37,99,235,0.45)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-blue-600 flex-shrink-0"></div>
                <span className="metric-label">USD/JPY</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('usdjpy')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'usdjpy' && (
                  <div className="metric-tooltip">
                    <div className="metric-tooltip-title">🇯🇵 엔케리 트레이드</div>
                    <div className="metric-tooltip-desc">일본에서 저금리로 엔화를 빌려, 미국 등 고금리 국가에 투자하는 전략입니다.</div>
                    <div className="metric-tooltip-item">• 엔화 약세(↑): 케리 트레이드 유리</div>
                    <div className="metric-tooltip-item">• 엔화 강세(↓): 케리 청산 위험</div>
                    <div className="metric-tooltip-item">• 145엔 이상: 정부 개입 우려</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="metric-value text-blue-700">
                {marketData.usdjpy?.toFixed(1)}
              </span>
              <span className={marketData.changes?.usdjpy > 0 ? 'metric-change-up' : 'metric-change-down'}>
                {marketData.changes?.usdjpy > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.usdjpy || 0).toFixed(2)}%
              </span>
            </div>
          </div>

          {/* 미국 2년 국채 */}
          <div className="metric-indicator-card" style={{borderLeftColor: 'rgba(220,38,38,0.45)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <TrendingUp size={13} className="text-red-500" />
                <span className="metric-label">2년 국채</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('us2y')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'us2y' && (
                  <div className="metric-tooltip" style={{right: 'auto', left: 0}}>
                    <div className="metric-tooltip-title">📈 미국 2년 국채</div>
                    <div className="metric-tooltip-desc">단기 금리 동향과 연준 통화정책 방향성을 보여주는 핵심 지표입니다.</div>
                    <div className="metric-tooltip-item">• 상승: 인플레이션 우려, 금리 인상 기대</div>
                    <div className="metric-tooltip-item">• 하락: 경기 둔화, 금리 인하 기대</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="metric-value text-red-600">
                {marketData.us_2y_yield?.toFixed(1)}%
              </span>
              <span className={marketData.changes?.us_2y > 0 ? 'metric-change-down' : 'metric-change-up'}>
                {marketData.changes?.us_2y > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.us_2y || 0).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* 미국 10년 국채 */}
          <div className="metric-indicator-card" style={{borderLeftColor: 'rgba(79,70,229,0.45)'}}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <TrendingUp size={13} className="text-indigo-500" />
                <span className="metric-label">10년 국채</span>
              </div>
              <button className="relative" onMouseEnter={() => setShowTooltip('us10y')} onMouseLeave={() => setShowTooltip(null)}>
                <Info size={10} className="text-gray-300 hover:text-gray-500" />
                {showTooltip === 'us10y' && (
                  <div className="metric-tooltip">
                    <div className="metric-tooltip-title">📉 미국 10년 국채</div>
                    <div className="metric-tooltip-desc">장기 금리 동향과 경제 성장 기대를 나타내는 글로벌 기준 금리입니다.</div>
                    <div className="metric-tooltip-item">• 상승: 인플레이션·성장 기대, 주식 부담</div>
                    <div className="metric-tooltip-item">• 하락: 경기 둔화 우려, 주식 긍정적</div>
                    <div className="metric-tooltip-item">• 2년-10년 역전: 침체 신호</div>
                  </div>
                )}
              </button>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="metric-value text-indigo-600">
                {marketData.us_10y_yield?.toFixed(1)}%
              </span>
              <span className={marketData.changes?.us_10y > 0 ? 'metric-change-down' : 'metric-change-up'}>
                {marketData.changes?.us_10y > 0 ? '▲' : '▼'} {Math.abs(marketData.changes?.us_10y || 0).toFixed(1)}%
              </span>
            </div>
          </div>

        </div>
      ) : null}
        <div className="text-xs text-gray-400 text-right">
          {loading ? 'Loading...' : `${mainContentData?.createDateTime ? new Date(mainContentData?.createDateTime).toLocaleString('ko-KR') : 'N/A'}`}
        </div>
        </div>
      ) : null}
      {!mainContentLoading && (
        <button className="view-full-button mb-4"
            onClick={() => setIsFullAnalysisModalOpen(true)}>
            <span>전체 분석 보기</span>
            <ChevronRight size={16} />
        </button>
      )}

      </div>
  );

  const renderRankingSection = () => (
    !rankingLoading && rankingData && (
      <RankingSection
        rankingData={rankingData}
        onStockClick={handleClick}
        onOpenRanking={() => setShowFullRanking(true)}
      />
    )
  );

  const renderFavoritesSection = () => (
    <div className="feature-section">
      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon">
          <ScanSearch size={20} />
        </div>
        <h2 className="text-xm font-bold text-black">관심 기업</h2>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">
        즐겨찾기한 주식들의 실시간 가격과 변동률을 확인하고 빠른 투자 결정을 내리세요.
      </p>

      {favoriteLoading ? (
        <div className="card animate-pulse">
          <div className="flex justify-between items-center py-3 border-b border-gray-100">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded mb-1 w-16"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
              <div className="h-20 bg-gray-200 rounded mt-2"></div>
            </div>
          </div>
          <div className="flex justify-between items-center py-3">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded mb-1 w-16"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
              <div className="h-20 bg-gray-200 rounded mt-2"></div>
            </div>
          </div>
        </div>
      ) : hasFavorites && favoriteList.length > 0 ? (
        <div className="card">
          {favoriteList.map((favorite, index) => (
            <div
              key={favorite.userFavoriteTickerNo || index}
              className={`flex justify-between items-center py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                index < favoriteList.length - 1 ? 'border-b border-gray-100' : ''
              }`}
              onClick={() => handleClick(favorite.ticker)}
            >
              <div className="flex items-center">

                <div>
                  <div className="flex items-start">
                    <div className="stock-icon"
                        style={{ width: '60px', height: '60px'}}>
                      <img
                        src={getStockLogoPath(favorite.ticker)}
                        alt={favorite.nameKr || favorite.ticker}
                      />
                      <TrendingUp size={20} className="fallback-icon" />
                    </div>
                    <div className="stock-info">
                      <div className="stock-symbol">{favorite.ticker}</div>
                        <div className="stock-name">
                          {favorite.nameKr && <div>{favorite.nameKr}</div>}
                          {favorite.nameEn && <div>{favorite.nameEn}</div>}
                        </div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <MiniTradingView symbol={`${favorite.ticker}`} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="add-favorites-card">
          <Star className="add-star text-blue-400" />
          <h3 className="add-favorites-title">즐겨찾기 추가</h3>
          <p className="add-favorites-desc">
            관심 있는 주식을 즐겨찾기에 추가하여 빠르게 확인하세요
          </p>
          <button
            className="view-full-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openSearchModal(e);
            }}
          >
            <Search size={16} />
            <span>주식 검색</span>
          </button>
        </div>
      )}
    </div>
  );

  const renderFearGreedSection = () => (
    <div className="feature-section">

      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon">
          <Activity size={20} />
        </div>
        <h2 className="text-xm font-bold text-black">공포 탐욕 지수</h2>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">
        시장 투자자들의 감정 상태를 측정하여 현재 시장이 과매수인지 과매도인지 파악하세요.
      </p>

      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {loading ? 'Loading...' : (() => {
                const score = fearGreedData?.score || 0;
                if (score <= 25) return '현재 공포탐욕지수: 극심한 공포';
                if (score <= 45) return '현재 공포탐욕지수: 공포';
                if (score <= 55) return '현재 공포탐욕지수: 중립';
                if (score <= 75) return '현재 공포탐욕지수: 탐욕';
                return '현재 공포탐욕: 과한 탐욕';
              })()}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {loading ? 'Fetching data...' : (() => {
                const score = fearGreedData?.score || 0;
                if (score <= 25) return '투자자들이 매우 공포에 빠져 있습니다';
                if (score <= 45) return '투자자들이 공포를 느끼고 있습니다';
                if (score <= 55) return '투자자들이 중립적인 상태입니다';
                if (score <= 75) return '투자자들이 탐욕을 느끼고 있습니다';
                return '투자자들이 매우 탐욕적입니다';
              })()}
            </p>
          </div>
          <div className="flex items-center">
            <span className={`text-2xl font-bold ml-2 ${loading ? 'text-gray-900' : (() => {
                const score = fearGreedData?.score || 0;
                if (score <= 25) return 'text-red-500';
                if (score <= 45) return 'text-orange-500';
                if (score <= 55) return 'text-yellow-500';
                if (score <= 75) return 'text-green-500';
                return 'text-green-700';
              })()}`}>
              {loading ? 'Loading...' : fearGreedData?.score.toFixed(0) || 'N/A'}
            </span>
          </div>
        </div>

        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2 relative">
          <div className="flex h-full">
            <div style={{ width: '25%' }} className="bg-red-500"></div>
            <div style={{ width: '20%' }} className="bg-orange-500"></div>
            <div style={{ width: '10%' }} className="bg-yellow-400"></div>
            <div style={{ width: '20%' }} className="bg-green-500"></div>
            <div style={{ width: '25%' }} className="bg-green-700"></div>
          </div>
          {!loading && fearGreedData && (
            <div
              className="absolute top-0 w-1 h-full bg-black z-10"
              style={{
                left: `${fearGreedData.score}%`,
                transform: 'translateX(-50%)'
              }}
            />
          )}
        </div>

        <div className="flex justify-between mb-4 text-xs text-gray-500">
          <span>극심한 공포</span>
          <span>공포</span>
          <span>중립</span>
          <span>탐욕</span>
          <span>과한 탐욕</span>
        </div>

        <div className="text-xs text-gray-400 text-right">
          {loading ? 'Loading...' : `${fearGreedData?.KST ? new Date(fearGreedData.KST).toLocaleString('ko-KR') : 'N/A'}`}
        </div>
      </div>
    </div>
  );

  const renderNewsSection = () => (
    <NewsSection></NewsSection>
  );

  const renderTwitterSection = () => (
    <div className="feature-section">
      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon">
          <Twitter size={20} />
        </div>
        <h2 className="text-xm font-bold text-black">주요 인물 SNS</h2>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">
        주요 인물들의 X(트위터)를 통해 최신 시장 동향과 리스크를 실시간으로 확인하세요.
      </p>

      {xTweetLoading ? (
        <div className="bg-white rounded-lg p-6 animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-16"></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-4/5"></div>
            <div className="h-4 bg-gray-200 rounded w-3/5"></div>
          </div>
        </div>
      ) : xTweetData.length > 0 ? (
        <div className="space-y-4">
          {xTweetData.map((tweet, index) => (
            <TwitterTweetEmbed
              key={`${tweet.xinfoNo}-${theme}`}
              tweetId={tweet.xid}
              options={{ theme: theme === 'dark' ? 'dark' : 'light' }}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg p-6 text-center">
          <div className="text-gray-500 mb-2">트윗을 불러올 수 없습니다</div>
          <button
            onClick={fetchXTweetData}
            className="text-blue-600 text-sm hover:underline"
          >
            다시 시도
          </button>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    document.title = '투자특공대 — AI 주식 분석';
    return () => { document.title = '투자특공대'; };
  }, []);

  // 컴포넌트 마운트 시 API 호출
  useEffect(() => {
    // OAuth 성공 여부 URL 파라미터 체크
    const params = new URLSearchParams(window.location.search);
    const isOAuthSuccess = params.get('successYn') === 'Y';
    console.log('[useEffect] mount, url:', window.location.href, 'isOAuthSuccess:', isOAuthSuccess);

    // 강제 업데이트 체크 (iOS 2.0.0 미만)
    if (getPlatform() === 'ios') {
      getAppVersion().then((version) => {
        if (isVersionBelow(version, '2.0.0')) {
          showAlert('error', `현재 버전(${version})은 더 이상 지원되지 않아요.\n최신 버전으로 업데이트해주세요.`, {
            autoClose: false,
            confirmLabel: '업데이트',
            cancelable: false,
            dismissable: false,
            onConfirm: () => {
              if (window.webkit?.messageHandlers?.openExternalUrl) {
                window.webkit.messageHandlers.openExternalUrl.postMessage('https://apps.apple.com/app/6748879356'); // iOS
              }
            },
          });
        }
      });
    }

    initAuth(isOAuthSuccess);
    checkFirstVisitOnboarding();
    checkWebFirstVisit();
    fetchFearGreedIndexData();
    fetchMainContent();
    fetchMarketData();
    fetchXTweetData();
    fetchRankingData();
  }, []);


  return (
    <div className="main-container">
      {/* 상단 헤더 */}
      <header className="header">
        <div className="header-content">
          <div className="header-row">
            <div className="logo-container">
              <img src="/logo.png" alt="Logo" className="logo" />
              <div className="header-brand">
                <span className="header-brand-name">투자특공대</span>
                <span className="header-brand-sub">AI Stock Analysis</span>
              </div>
            </div>
            <div className="header-actions">
              <div
                className="icon-button"
                role="button"
                tabIndex={0}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openSearchModal(e);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Search size={16} />
              </div>
              <button type="button" className="icon-button" onClick={toggleTheme}>
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              {getPlatform() === 'web' && (
                <button type="button" className="icon-button" onClick={() => navigate('/community')}>
                  <MessageSquare size={16} />
                </button>
              )}
              <button type="button" className="icon-button" onClick={goToProfile}>
                <User size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="main-content">

      {/* 투자 면책 조항 */}
      <InvestmentDisclaimer />

      {/* 시장 분석 */}
      {renderAiAnalysisSection()}

      {/* 주식 랭킹 */}
      {renderRankingSection()}

      {/* 관심 기업 */}
      {renderFavoritesSection()}

      {/* Reddit 트렌딩 */}
      <RedditBuzzBubble />

      {/* Polymarket 예측 */}
      <PolymarketBuzz />

      {/* 공포 탐욕 지수 */}
      {renderFearGreedSection()}

      {/* 공포 탐욕 지수 히스토리 */}
      <FearGreedHistoryChart />

      {/* 경기 침체 위험 지수 (FRED 9개 지표) */}
      <RecessionAlert />

      {/* 뉴스 */}
      {renderNewsSection()}

      {/* X(트위터) */}
      {renderTwitterSection()}

{/* 다가오는 이벤트 섹션 */}
{/* <div className="feature-section">
  <div className="feature-icon">
    <Calendar size={24} />
  </div>
  <h2 className="text-xl font-bold mb-2 text-black">중요한 이벤트</h2>
  <p className="text-sm text-gray-600 mb-4">
    다가오는 중요한 경제 이벤트와 실적 발표 일정을 미리 확인하고 대비하세요.
  </p>
  
  <div className="card">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Quadruple Witching Day
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          The simultaneous expiration of stock index futures, stock index options, 
          stock options, and single stock futures on the third Friday of March, June, 
          September, and December...
        </p>
      </div>
    </div>
    <div className="flex justify-between items-center text-xs">
      <span className="text-gray-400">June 21, 2025</span>
      </div>
  </div>

  <div className="card mt-4">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Fed Interest Rate Decision
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          The Federal Open Market Committee (FOMC) announces its decision 
          on interest rates. Market expects rates to remain unchanged at 5.25-5.50%...
        </p>
      </div>
    </div>
    <div className="flex justify-between items-center text-xs">
      <span className="text-gray-400">June 26, 2025</span>
      </div>
  </div>
</div> */}

{/* 주식 용어 팁 섹션 */}
{/* <div className="feature-section">
  <div className="feature-icon">
    <BookOpen size={24} />
  </div>
  <h2 className="text-xl font-bold mb-2 text-black">투자 지식</h2>
  <p className="text-sm text-gray-600 mb-4">
    주식 투자에 필요한 핵심 용어와 개념을 쉽게 이해하고 투자 실력을 향상시키세요.
  </p>
  
  <div className="card">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          What is Quadruple Witching?
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          "Quadruple Witching" refers to the simultaneous expiration of stock index futures, 
          stock index options, stock options, and single stock futures. This happens four times 
          a year on the third Friday of March, June, September, and December. These days typically 
          see increased trading volume and volatility...
        </p>
      </div>
    </div>
    <div className="flex justify-end">
      <button className="text-xs text-blue-600 hover:underline">Read more</button>
    </div>
  </div>

  <div className="card mt-4">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Understanding Price-to-Earnings (P/E) Ratio
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          The Price-to-Earnings (P/E) ratio is a valuation metric that compares a company's 
          current share price to its earnings per share (EPS). A high P/E ratio could mean 
          a stock is overvalued or that investors expect high growth rates in the future...
        </p>
      </div>
    </div>
    <div className="flex justify-end">
      <button className="text-xs text-blue-600 hover:underline">Read more</button>
    </div>
  </div>
</div> */}
      </main>

      {/* 온보딩 모달 */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={handleOnboardingClose}
      />

      {/* 랭킹 모달 */}
      <RankingModal
        isOpen={showFullRanking}
        onClose={() => setShowFullRanking(false)}
        rankingData={rankingData}
        onStockClick={handleClick}
      />

      {/* 모달 */}
      <FullAnalysisModal
        isOpen={isFullAnalysisModalOpen} 
        onClose={() => setIsFullAnalysisModalOpen(false)} 
        analysisText={mainContentData?.mainContent || "시장 분석 데이터를 불러오는 중입니다..."}
        stockSymbol="시장 전체"
        reportType="market"
      />

      {/* 검색 모달 */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={closeSearchModal}
        onStockClick={handleClick}
      />
      
      {/* 앱 다운로드 유도 Bottom Sheet */}
      {showAppDownloadSheet && (
        <div className="app-promo-overlay" onClick={closeAppDownloadSheet}>
          <div className="app-promo-sheet" onClick={e => e.stopPropagation()}>
            {/* 드래그 핸들 */}
            <div className="app-promo-handle" />

            {/* 헤더: 로고 + 닫기 */}
            <div className="app-promo-header">
              <div className="app-promo-logo-wrap">
                <img src="/logo.png" alt="투자특공대" className="app-promo-logo" />
              </div>
              <button onClick={closeAppDownloadSheet} className="app-promo-close">
                <X size={16} />
              </button>
            </div>

            {/* 본문 */}
            <div className="app-promo-body">
              <h3 className="app-promo-title">앱에서 더 나은 경험을</h3>
              <p className="app-promo-subtitle">AI 투자 분석을 더 빠르고 편리하게</p>

              <div className="app-promo-features">
                <div className="app-promo-feature">
                  <div className="app-promo-feature-icon app-promo-icon--blue">
                    <Star size={14} fill="currentColor" />
                  </div>
                  <span>관심 종목 즐겨찾기</span>
                </div>
                <div className="app-promo-feature">
                  <div className="app-promo-feature-icon app-promo-icon--purple">
                    <Zap size={14} />
                  </div>
                  <span>더 빠른 로딩 속도</span>
                </div>
                <div className="app-promo-feature">
                  <div className="app-promo-feature-icon app-promo-icon--green">
                    <MessageSquare size={14} />
                  </div>
                  <span>커뮤니티 이용 가능</span>
                </div>
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="app-promo-actions">
              <button onClick={goToAppStore} className="app-promo-btn-primary">
                <Download size={18} />
                앱 다운로드
              </button>
              <button onClick={closeAppDownloadSheet} className="app-promo-btn-secondary">
                나중에 하기
              </button>
            </div>
          </div>
        </div>
      )}

      <ContactFooter />

      <AlertModal alert={alert} onClose={closeAlert} />
    </div>
  );
};

export default StockMainPage;
